/**
 * useGenerateJournalContent - Hook for populating an existing journal with AI-generated content
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { config } from "@/lib/config";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GenerateJournalOptions {
    generate_youtube_videos?: boolean;
    youtube_videos_per_module?: number;
    generate_quizzes?: boolean;
    quiz_question_count?: number;
    quiz_difficulty?: "easy" | "medium" | "hard";
    quiz_type?: string;
    generate_todos?: boolean;
    share_to_community?: boolean;
    max_modules?: number;
}

interface GenerateJournalContentRequest {
    journal_id: string;  // Existing journal to populate
    topic_prompt: string;
    options?: GenerateJournalOptions;
}

interface ModuleStatus {
    module_title: string;
    note_block_id: string | null;
    youtube_block_ids: string[];
    quiz_block_id: string | null;
    status: "pending" | "generating" | "complete" | "error";
    error: string | null;
}

interface GenerateJournalResponse {
    success: boolean;
    journal_id: string;
    journal_name: string;
    modules_created: number;
    blocks_created: number;
    todos_created: number;
    shared: boolean;
    generation_time_seconds: number;
    module_statuses: ModuleStatus[];
    errors: string[];
}

export const useGenerateJournalContent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: GenerateJournalContentRequest): Promise<GenerateJournalResponse> => {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            const userId = session?.user?.id;

            if (!userId) {
                throw new Error("User not authenticated");
            }

            const response = await fetch(
                `${config.BACKEND_API_URL}/api/journals/generate-comprehensive`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token && { Authorization: `Bearer ${token}` }),
                    },
                    body: JSON.stringify({
                        user_id: userId,  // Still needed for backend validation
                        journal_id: request.journal_id,
                        topic_prompt: request.topic_prompt,
                        options: request.options,
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Generation failed: ${response.status} - ${errorText}`);
            }

            return response.json();
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["journals"] });
            queryClient.invalidateQueries({ queryKey: ["journal", variables.journal_id] });
            queryClient.invalidateQueries({ queryKey: ["journal-blocks", variables.journal_id] });

            if (data.success) {
                toast.success(`Generated ${data.modules_created} modules for "${data.journal_name}"!`);
            } else {
                toast.warning(`Generation completed with issues: ${data.errors.join(", ")}`);
            }
        },
        onError: (error) => {
            console.error("Generate journal content error:", error);
            toast.error("Failed to generate journal content");
        },
    });
};

// Keep backward compatibility (optional - can be removed later)
export const useGenerateJournal = useGenerateJournalContent;

// Export types
export type { GenerateJournalContentRequest, GenerateJournalResponse, GenerateJournalOptions, ModuleStatus };
